"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  QuantityField,
  WorkingSteps,
} from "@/components/tools/physics-waves/quantity-field";
import {
  SoundPanel,
  StringPanel,
  WaveFigure,
  type WavePanelStrings,
} from "@/components/tools/physics-waves/wave-panels";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type NumberFormatter,
  withSiPrefix,
} from "@/lib/tool-kit/physics-waves/format";
import {
  findUnit,
  HERTZ_UNITS,
  METER_UNITS,
  SECOND_UNITS,
  SPEED_UNITS,
  toBase,
  type UnitDef,
} from "@/lib/tool-kit/physics-waves/units";
import { solveWave, type WaveValues } from "@/lib/tool-kit/physics-waves/waves";

/** English strings. A translated content file overrides these through `ui`. */
export const WAVE_SPEED_CALCULATOR_STRINGS = {
  modeLabel: "What kind of wave?",
  modes: {
    general: "Any wave: v = fλ",
    string: "Wave on a string",
    sound: "Sound in air",
  },
  unitFor: "Unit for {name}",
  sigFigs: "Round to this many significant figures",
  results: "Results",
  enterAll: "Enter a number in every box to see the result.",
  needPositive: "Every value must be a number above zero.",
  working: "The working",
  goalLabel: "What do you want to find?",
  goals: {
    v: "Wave speed",
    f: "Frequency",
    lambda: "Wavelength",
    T: "Period",
  },
  givenLabel: "Which do you know?",
  givenFrequency: "Frequency",
  givenPeriod: "Period",
  names: {
    v: "Wave speed (v)",
    f: "Frequency (f)",
    lambda: "Wavelength (λ)",
    T: "Period (T)",
  },
  angular: "Angular frequency (ω)",
  wavenumber: "Wavenumber (k)",
  stepPeriod: "f = 1 ÷ T = 1 ÷ {t} = {f}",
  stepSpeed: "v = f × λ = {f} × {lambda} = {v}",
  stepFrequency: "f = v ÷ λ = {v} ÷ {lambda} = {f}",
  stepWavelength: "λ = v ÷ f = {v} ÷ {f} = {lambda}",
  stepPeriodResult: "T = 1 ÷ f = 1 ÷ {f} = {t}",
  figureLabel: "A wave with one wavelength marked",
  figureDescription:
    "Two cycles of a sine wave with one wavelength marked: {value}.",
  wavelengthTag: "λ = {value}",
  stringGoalLabel: "What do you want to find?",
  stringGoals: {
    v: "Wave speed",
    tension: "Tension",
    density: "Mass per length",
  },
  stringSpeed: "Wave speed (v)",
  stringTension: "Tension (F)",
  stringDensity: "Linear density (μ)",
  stringLength: "String length (optional)",
  stringLengthHint:
    "Add the length between the fixed ends to see the standing waves.",
  stringStepSpeed: "v = √(F ÷ μ) = √({f} ÷ {mu}) = {v}",
  stringStepTension: "F = μ × v² = {mu} × ({v})² = {f}",
  stringStepDensity: "μ = F ÷ v² = {f} ÷ ({v})² = {mu}",
  harmonicsHeading: "Standing waves on the string",
  columnHarmonic: "Mode",
  columnFrequency: "Frequency",
  columnWavelength: "Wavelength",
  harmonicName: "Harmonic {n}",
  fundamentalName: "Fundamental",
  soundTemperature: "Air temperature",
  soundUnitLabel: "Temperature scale",
  temperatureUnits: { C: "°C", K: "K", F: "°F" },
  soundDelay: "Delay before you hear it (optional)",
  soundDelayHint: "Time between a flash and its sound gives the distance.",
  soundSpeed: "Speed of sound",
  soundSpeedKmh: "In kilometres per hour",
  soundSpeedMph: "In miles per hour",
  soundLinear: "Straight-line approximation",
  soundDistance: "Distance travelled in that time",
  soundStep: "v = √(γ × R × T) = √(1.4 × 287.05 × {k} K) = {v} m/s",
  soundStepLinear: "v ≈ 331.3 + 0.606 × θ = 331.3 + 0.606 × {c} = {v} m/s",
  soundBelowZero:
    "Temperatures at or below absolute zero (−273.15 °C) do not have a speed of sound.",
  soundGraphLabel: "Speed of sound against temperature",
  soundGraphDescription:
    "The speed of sound in dry air rises from about 306 metres per second at −40 degrees Celsius to about 360 at 50 degrees.",
  soundGraphX: "Temperature (°C)",
  soundGraphY: "Speed (m/s)",
} as const;

type Strings = typeof WAVE_SPEED_CALCULATOR_STRINGS;
type Mode = keyof Strings["modes"];
type Goal = keyof Strings["goals"];

const SIG_CHOICES = ["3", "4", "5"] as const;
const WAVELENGTH_UNITS: UnitDef[] = METER_UNITS;
const UNITS_FOR: Record<"v" | "f" | "lambda" | "T", UnitDef[]> = {
  v: SPEED_UNITS,
  f: HERTZ_UNITS,
  lambda: WAVELENGTH_UNITS,
  T: SECOND_UNITS,
};

interface Entry {
  value: string;
  unit: string;
}

type Entries = Record<"v" | "f" | "lambda" | "T", Entry>;

const INITIAL: Entries = {
  v: { value: "343", unit: "m/s" },
  f: { value: "440", unit: "Hz" },
  lambda: { value: "0.78", unit: "m" },
  T: { value: "2.27", unit: "ms" },
};

function stepLines(
  goal: Goal,
  givenPeriod: boolean,
  period: number | null,
  r: WaveValues,
  strings: Strings,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const show = (value: number, unit: string) =>
    withSiPrefix(fmt, value, unit, sig);
  const text = {
    v: show(r.v, "m/s"),
    f: show(r.f, "Hz"),
    lambda: show(r.lambda, "m"),
    t: show(period ?? r.T, "s"),
  };
  const lines: string[] = [];
  if (givenPeriod && goal !== "T" && goal !== "f") {
    lines.push(t(strings.stepPeriod, text));
  }
  if (goal === "v") {
    lines.push(t(strings.stepSpeed, text));
  } else if (goal === "lambda") {
    lines.push(t(strings.stepWavelength, text));
  } else {
    lines.push(t(strings.stepFrequency, text));
    if (goal === "T") {
      lines.push(t(strings.stepPeriodResult, text));
    }
  }
  return lines;
}

function GeneralResults({
  goal,
  result,
  lines,
  strings,
  fmt,
  sig,
}: {
  goal: Goal;
  result: WaveValues;
  lines: string[];
  strings: Strings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const show = (value: number, unit: string) =>
    withSiPrefix(fmt, value, unit, sig);
  const emphasised = (key: Goal) =>
    key === goal || (goal === "T" && key === "f");
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis={emphasised("v")}
          label={strings.names.v}
          value={show(result.v, "m/s")}
        />
        <StatCard
          emphasis={emphasised("f")}
          label={strings.names.f}
          value={show(result.f, "Hz")}
        />
        <StatCard
          emphasis={emphasised("lambda")}
          label={strings.names.lambda}
          value={show(result.lambda, "m")}
        />
        <StatCard
          emphasis={goal === "T"}
          label={strings.names.T}
          value={show(result.T, "s")}
        />
        <StatCard label={strings.angular} value={show(result.omega, "rad/s")} />
        <StatCard label={strings.wavenumber} value={show(result.k, "rad/m")} />
      </StatGrid>
      <WaveFigure
        fmt={fmt}
        sig={sig}
        strings={strings}
        wavelength={result.lambda}
      />
      <WorkingSteps heading={strings.working} lines={lines} />
    </>
  );
}

function GeneralPanel({
  strings,
  fmt,
  sig,
}: {
  strings: Strings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const [goal, setGoal] = useState<Goal>("lambda");
  const [given, setGiven] = useState<"f" | "T">("f");
  const [entries, setEntries] = useState(INITIAL);
  const set = (key: keyof Entries, patch: Partial<Entry>) =>
    setEntries((current) => ({
      ...current,
      [key]: { ...current[key], ...patch },
    }));
  const read = (key: keyof Entries): number | null => {
    const parsed = parseNumberInput(entries[key].value);
    return parsed === null
      ? null
      : toBase(parsed, findUnit(UNITS_FOR[key], entries[key].unit));
  };

  const wantsGiven = goal === "v" || goal === "lambda";
  const keys = ((): (keyof Entries)[] => {
    if (goal === "v") {
      return ["lambda", given];
    }
    if (goal === "lambda") {
      return ["v", given];
    }
    return ["v", "lambda"];
  })();
  const period = given === "T" && wantsGiven ? read("T") : null;
  const frequency = period !== null && period > 0 ? 1 / period : read("f");
  const known = {
    v: read("v") ?? undefined,
    f: frequency ?? undefined,
    lambda: read("lambda") ?? undefined,
  };
  const target = goal === "T" ? "f" : goal;
  const used = keys.map(read);
  const result = used.every((x) => x !== null)
    ? solveWave(target, known)
    : null;

  return (
    <>
      <PillGroup
        label={strings.goalLabel}
        name="ws-goal"
        onChange={setGoal}
        options={(Object.keys(strings.goals) as Goal[]).map((value) => ({
          value,
          label: strings.goals[value],
        }))}
        value={goal}
      />
      {wantsGiven ? (
        <PillGroup
          label={strings.givenLabel}
          name="ws-given"
          onChange={setGiven}
          options={[
            { value: "f", label: strings.givenFrequency },
            { value: "T", label: strings.givenPeriod },
          ]}
          value={given}
        />
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        {keys.map((key) => (
          <QuantityField
            id={`ws-${key}`}
            key={key}
            label={strings.names[key]}
            onChange={(value) => set(key, { value })}
            onUnitChange={(unit) => set(key, { unit })}
            unit={entries[key].unit}
            unitLabel={t(strings.unitFor, { name: strings.names[key] })}
            units={UNITS_FOR[key]}
            value={entries[key].value}
          />
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {used.some((x) => x === null) ? (
          <Notice>{strings.enterAll}</Notice>
        ) : null}
        {used.every((x) => x !== null) && result === null ? (
          <ErrorNotice>{strings.needPositive}</ErrorNotice>
        ) : null}
        {result === null ? null : (
          <GeneralResults
            fmt={fmt}
            goal={goal}
            lines={stepLines(
              goal,
              given === "T" && wantsGiven,
              period,
              result,
              strings,
              fmt,
              sig
            )}
            result={result}
            sig={sig}
            strings={strings}
          />
        )}
      </section>
    </>
  );
}

export default function WaveSpeedCalculatorWidget() {
  const strings = useToolUi(WAVE_SPEED_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("general");
  const [sigText, setSigText] = useState<(typeof SIG_CHOICES)[number]>("4");
  const sig = Number(sigText);
  const panelStrings: WavePanelStrings = strings;

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="ws-mode"
        onChange={setMode}
        options={(Object.keys(strings.modes) as Mode[]).map((value) => ({
          value,
          label: strings.modes[value],
        }))}
        value={mode}
      />
      <div className="max-w-xs">
        <SelectField
          id="ws-sig"
          label={strings.sigFigs}
          onChange={setSigText}
          options={SIG_CHOICES.map((value) => ({ value, label: value }))}
          value={sigText}
        />
      </div>
      {mode === "general" ? (
        <GeneralPanel fmt={fmt} sig={sig} strings={strings} />
      ) : null}
      {mode === "string" ? (
        <StringPanel fmt={fmt} sig={sig} strings={panelStrings} />
      ) : null}
      {mode === "sound" ? (
        <SoundPanel fmt={fmt} sig={sig} strings={panelStrings} />
      ) : null}
    </>
  );
}
